import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "@/api";
import { setMetaTags, SITE_URL } from "@/seo";
import { Reveal, ArrowButton } from "@/components/bits";
import { formatDate } from "./Insights";
import NewsletterSignup from "@/components/NewsletterSignup";

export default function InsightArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(undefined);

  useEffect(() => {
    setArticle(undefined);
    api
      .get(`/articles/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => setArticle(null));
  }, [slug]);

  useEffect(() => {
    if (!article) return;
    setMetaTags({
      title: `${article.title} | The Nutrition Network Africa`,
      description: article.excerpt || `${article.title}. An insight from The Nutrition Network Africa on nutrition, technology and healthcare innovation.`,
      path: `/insights/${article.slug}`,
    });
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "article-jsonld";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt || undefined,
      image: article.cover ? `${SITE_URL}${article.cover}` : `${SITE_URL}/assets/og-image.png`,
      datePublished: article.created_at,
      dateModified: article.updated_at,
      author: { "@type": "Organization", name: "The Nutrition Network Africa", url: SITE_URL },
      publisher: {
        "@type": "Organization",
        name: "The Nutrition Network Africa",
        logo: { "@type": "ImageObject", url: `${SITE_URL}/assets/icon.png` },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/insights/${article.slug}` },
    });
    document.head.appendChild(script);
    return () => document.getElementById("article-jsonld")?.remove();
  }, [article]);

  if (article === null) return <Navigate to="/insights" replace />;
  if (article === undefined) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center pt-24" data-testid="article-loading">
        <span className="text-xs font-bold tracking-[0.2em] uppercase text-tnn-muted">Loading</span>
      </div>
    );
  }

  const paragraphs = article.body.split(/\n\n+/).filter(Boolean);

  const renderBlock = (block, i) => {
    const trimmed = block.trim();
    if (/^(\/api\/uploads\/|https?:\/\/)\S+\.(png|jpe?g|webp|gif)$/i.test(trimmed)) {
      return (
        <img
          key={i}
          src={trimmed}
          alt="Article image"
          loading="lazy"
          className="w-full rounded-3xl border border-tnn-ink/8"
        />
      );
    }
    if (trimmed.startsWith("## ")) {
      return (
        <h2 key={i} className="pt-5 font-display text-2xl font-bold tracking-tight text-tnn-ink">
          {trimmed.slice(3)}
        </h2>
      );
    }
    const lines = block.split("\n").filter(Boolean);
    if (lines.length > 1 && lines.every((l) => /^\d+\.\s/.test(l))) {
      return (
        <ol key={i} className="list-decimal pl-6 space-y-2">
          {lines.map((l, j) => (
            <li key={j}>{l.replace(/^\d+\.\s/, "")}</li>
          ))}
        </ol>
      );
    }
    return <p key={i}>{block}</p>;
  };

  return (
    <main data-testid={`article-${article.slug}`}>
      <article className="bg-tnn-warm">
        <div className="max-w-3xl mx-auto px-6 pt-32 lg:pt-40 pb-16 lg:pb-24">
          <Reveal>
            <Link
              to="/insights"
              data-testid="article-back"
              className="inline-flex items-center gap-1.5 text-xs font-bold tracking-[0.18em] uppercase text-tnn-purple hover:text-tnn-deep transition-colors duration-200"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Insights
            </Link>
          </Reveal>
          <Reveal delay={0.06}>
            <div className="mt-8 flex items-center gap-3">
              <span className="text-[10px] font-bold tracking-[0.14em] uppercase px-2.5 py-1 rounded-full bg-tnn-soft text-tnn-purple">
                {article.topic}
              </span>
              <span className="text-xs text-tnn-muted">{formatDate(article.created_at)}</span>
            </div>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-tnn-ink leading-[1.12]">
              {article.title}
            </h1>
          </Reveal>
          {article.cover && (
            <Reveal delay={0.12}>
              <img
                src={article.cover}
                alt={article.title}
                data-testid="article-cover"
                className="mt-10 w-full rounded-3xl border border-tnn-ink/8 object-cover max-h-[420px]"
              />
            </Reveal>
          )}
          <Reveal delay={0.16}>
            <div className="mt-10 space-y-5 text-[17px] leading-relaxed text-tnn-ink/80" data-testid="article-body">
              {paragraphs.map(renderBlock)}
            </div>
          </Reveal>
          <NewsletterSignup />
        </div>
      </article>

      <section className="bg-tnn-warm pb-16 lg:pb-24">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <div className="bg-tnn-deep rounded-[2rem] px-8 py-12 lg:p-14 flex flex-col sm:flex-row sm:items-center justify-between gap-8">
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white leading-[1.2] max-w-sm">
                Working on something in this space?
              </h2>
              <ArrowButton to="/contact" variant="white" testid="article-cta">
                Partner With TNN Africa
              </ArrowButton>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
